import { AnyAction } from 'redux';
import { fork, put, take } from 'redux-saga/effects';

import { BaseApiRequest } from '../../api/generic/baseApiRequest';
import * as EntityNormalizers from '../../api/normalizers/entityNormalizers';
import { DataRequestActionTypes } from '../../constants/actionTypes';
import * as SessionHelper from '../../helpers/sessionHelper';
import { RequestAction } from '../allActions';
import { createRequestLoadingErrorAction, createRequestSuccessfulAction } from '../errorActions';
import { createLogoutAction } from '../logoutActions';
import { refreshToken } from '../sessionActions';

function shouldLogout(e: any) {
  return e.message === 'Invalid token';
}

export function* runApiRequest<T>(
  request: BaseApiRequest<T, EntityNormalizers.NormalizedEntity<T>>,
  actionBuilder?: (result: EntityNormalizers.NormalizedEntity<T>) => AnyAction,
): Generator<any, void, any> {
  function* sendRequest(): Generator<any, void, any> {
    const result = yield request.send();

    if (actionBuilder) {
      yield put(actionBuilder(result));
    }
    yield put(createRequestSuccessfulAction(request.component));
  }

  try {
    yield sendRequest();
  } catch (e) {
    if (e.message === 'jwt expired') {
      try {
        const sessionToken = SessionHelper.getParsedSessionToken();
        yield refreshToken(sessionToken.id, sessionToken.refreshToken, request.component, request.dispatch);
        yield sendRequest();
      } catch (ex) {
        yield put(createLogoutAction());
        yield put(createRequestLoadingErrorAction(request.component, ex));
      }
    } else if (shouldLogout(e)) {
      yield put(createLogoutAction());
    } else {
      yield put(createRequestLoadingErrorAction(request.component, e));
    }
  }
}

export function* throttleRequests<T extends RequestAction>(
  actionType: DataRequestActionTypes,
  callback: (action: T) => void,
  identifierAccessor: (action: T) => string,
): Generator<any, void, any> {
  const encounteredIdentifiers = new Set();

  function* callbackWrapper(identifier: string, action: T): Generator<any, void, any> {
    yield callback(action);
    encounteredIdentifiers.delete(identifier);
  }

  while (true) {
    const action: T = yield take(actionType);
    const identifier = identifierAccessor(action);

    if (!encounteredIdentifiers.has(identifier)) {
      encounteredIdentifiers.add(identifier);
      yield fork(callbackWrapper, identifier, action);
    }
  }
}
